import React, { memo, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import axios from 'axios';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import FormInput from 'CommonComponents/FormInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';
import SelectField from 'CommonComponents/SelectField';

import { useToast } from 'Hooks/useToast';

import PlusIcon from 'Assets/icons/plus.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const VAT_GST_FREQUENCY_OPTIONS = [
    { value: 'monthly', label: $t('Monthly') },
    { value: 'quarterly', label: $t('Quarterly') },
    { value: 'semi_annually', label: $t('Semi-Annually') },
    { value: 'annually', label: $t('Annually') },
];

function getFirstOfCurrentMonth() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
}

function newTaxLine() {
    return {
        id: null,
        name: '',
        calculation_method: 'percentage',
        rate: '',
        application_scope: null,
        frequency: null,
        payment_start_month: '',
    };
}

const TaxLineCard = memo(function TaxLineCard({
    line,
    onUpdate,
    onDelete,
    isSaving,
    calculationMethodOptions,
    applicationScopeOptions,
    frequencyOptions,
    currency,
    errors = {},
}) {
    const isOneOff = line.frequency === 'one_off';
    const isFixedAmount = line.calculation_method === 'fixed_amount' || isOneOff;
    const showApplicationScope = !isFixedAmount;

    const frequencyOption = line.frequency ? (frequencyOptions.find((o) => o.value === line.frequency) ?? null) : null;
    const calculationMethodOption = line.calculation_method ? (calculationMethodOptions.find((o) => o.value === line.calculation_method) ?? null) : null;
    const applicationScopeOption =
        showApplicationScope && line.application_scope ? (applicationScopeOptions.find((o) => o.value === line.application_scope) ?? null) : null;

    const rateLabel = isFixedAmount ? $t('Fixed Amount') : $t('Tax Rate');
    const paymentMonthLabel = isOneOff ? $t('Payment Month') : $t('Payment Start Month');
    const rateInputId = `tax-line-rate-${line.id ?? 'new'}`;

    const selectBorderClass = (hasError) => `taxline-select${hasError ? ' taxline-select--error' : ''}`;

    const handleCalculationMethodChange = (option) => {
        const method = option ? option.value : 'percentage';
        const updated = { ...line, calculation_method: method };
        if (method === 'fixed_amount') {
            updated.application_scope = null;
        }
        onUpdate(updated);
    };

    const handleFrequencyChange = (option) => {
        const freq = option ? option.value : null;
        const updated = { ...line, frequency: freq };
        if (freq === 'one_off') {
            updated.application_scope = null;
        }
        onUpdate(updated);
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-flex-1 tw-mr-8">
                    <input
                        type="text"
                        className={`text-input !tw-font-bold !tw-text-lg !tw-h-10 !tw-bg-transparent tw-cursor-pointer tw-w-full !tw-pl-1 ${errors.name ? 'tw-border tw-border-red-500' : 'tw-border-0'}`}
                        value={line.name}
                        onChange={(e) => onUpdate({ ...line, name: e.target.value })}
                        placeholder={$t('Tax Line Name')}
                        disabled={isSaving}
                    />
                    {errors.name && <div className="tw-text-red-500 tw-text-sm tw-mt-0.5">{errors.name}</div>}
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        type="button"
                        className="tw-text-red-500 hover:tw-text-red-700 tw-transition-colors"
                        onClick={onDelete}
                        disabled={isSaving}
                        title={$t('Delete tax line')}
                    >
                        <TrashIcon className="tw-w-4 tw-h-4" />
                    </button>
                </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                <div className="tw-flex tw-flex-row tw-justify-between tw-gap-4 tw-mb-2">
                    <div className="tw-flex tw-flex-col tw-w-full">
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 !tw-opacity-100">{$t('Calculation Method')}</label>
                        <SelectField
                            options={calculationMethodOptions}
                            value={calculationMethodOption ? calculationMethodOption.value : null}
                            onChange={handleCalculationMethodChange}
                            isDisabled={isSaving}
                            placeholder={$t('Select method')}
                            className={selectBorderClass(!!errors.calculation_method)}
                        />
                        {errors.calculation_method && <div className="tw-text-red-500 tw-text-sm tw-mt-1">{errors.calculation_method}</div>}
                    </div>

                    <div className="tw-flex tw-flex-col tw-w-full">
                        {isFixedAmount ? (
                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <label htmlFor={rateInputId} className="text-input__label !tw-opacity-100">
                                    {rateLabel}
                                </label>
                                <NumberFormat
                                    id={rateInputId}
                                    name={rateInputId}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    allowNegative={false}
                                    inputMode="numeric"
                                    className={`text-input !tw-bg-white ${errors.rate ? 'tw-border tw-border-red-500' : 'tw-border tw-border-gray-300'}`}
                                    placeholder="0.00"
                                    value={line.rate}
                                    disabled={isSaving}
                                    onValueChange={(values) => onUpdate({ ...line, rate: values.value ?? '' })}
                                />
                                {errors.rate && <div className="tw-text-red-500 tw-text-sm">{errors.rate}</div>}
                            </div>
                        ) : (
                            <FormInput
                                id={rateInputId}
                                name={rateInputId}
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                label={rateLabel}
                                value={line.rate}
                                disabled={isSaving}
                                onChange={(e) => onUpdate({ ...line, rate: e.target.value })}
                                placeholder="0.00"
                                suffix="%"
                                labelClassName="!tw-opacity-100"
                                inputClassName={`!tw-bg-white${!errors.rate ? ' !tw-border !tw-border-gray-300' : ''}`}
                                showError={!!errors.rate}
                                errorText={errors.rate}
                            />
                        )}
                    </div>

                    {showApplicationScope ? (
                        <div className="tw-flex tw-flex-col tw-w-full">
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 !tw-opacity-100">{$t('Application Scope')}</label>
                            <SelectField
                                options={applicationScopeOptions}
                                value={applicationScopeOption ? applicationScopeOption.value : null}
                                onChange={(option) => onUpdate({ ...line, application_scope: option ? option.value : null })}
                                isDisabled={isSaving}
                                placeholder={$t('Select scope')}
                                className={selectBorderClass(!!errors.application_scope)}
                            />
                            {errors.application_scope && <div className="tw-text-red-500 tw-text-sm tw-mt-1">{errors.application_scope}</div>}
                        </div>
                    ) : isOneOff ? (
                        <div className="tw-flex tw-flex-col tw-w-full">
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 !tw-opacity-100">{$t('Payment Frequency')}</label>
                            <SelectField
                                options={frequencyOptions}
                                value={frequencyOption ? frequencyOption.value : null}
                                onChange={handleFrequencyChange}
                                isDisabled={isSaving}
                                placeholder={$t('Select frequency')}
                                className={selectBorderClass(!!errors.frequency)}
                            />
                            {errors.frequency && <div className="tw-text-red-500 tw-text-sm tw-mt-1">{errors.frequency}</div>}
                        </div>
                    ) : (
                        <div className="tw-flex tw-flex-col tw-w-full" />
                    )}
                </div>

                <div className="tw-flex tw-flex-row tw-justify-between tw-gap-4 tw-mb-2">
                    {!isOneOff && (
                        <div className="tw-flex tw-flex-col tw-w-full">
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 !tw-opacity-100">{$t('Payment Frequency')}</label>
                            <SelectField
                                options={frequencyOptions}
                                value={frequencyOption ? frequencyOption.value : null}
                                onChange={handleFrequencyChange}
                                isDisabled={isSaving}
                                placeholder={$t('Select frequency')}
                                className={selectBorderClass(!!errors.frequency)}
                            />
                            {errors.frequency && <div className="tw-text-red-500 tw-text-sm tw-mt-1">{errors.frequency}</div>}
                        </div>
                    )}

                    <div className="tw-flex tw-flex-col tw-w-full">
                        <FormInput
                            id={`tax-line-start-month-${line.id ?? 'new'}`}
                            name={`tax-line-start-month-${line.id ?? 'new'}`}
                            type="date"
                            label={paymentMonthLabel}
                            value={line.payment_start_month}
                            disabled={isSaving}
                            onChange={(e) => {
                                let value = e.target.value;
                                if (value) {
                                    const parts = value.split('-');
                                    value = `${parts[0]}-${parts[1]}-01`;
                                }
                                onUpdate({ ...line, payment_start_month: value });
                            }}
                            labelClassName="!tw-opacity-100"
                            inputClassName={`!tw-bg-white${!errors.payment_start_month ? ' !tw-border !tw-border-gray-300' : ''}`}
                            showError={!!errors.payment_start_month}
                            errorText={errors.payment_start_month}
                        />
                        <p className="tw-text-gray-500 tw-text-sm tw-mt-1">{$t('Must be the first day of a month.')}</p>
                    </div>

                    {!isOneOff && <div className="tw-flex tw-flex-col tw-w-full" />}
                </div>
            </div>
            {(() => {
                const scopeHints = {
                    all_revenues: $t('The percentage is calculated on the total of all revenue sources (Revenue, Sponsorship, Ticketing).'),
                    all_expenditure: $t('The percentage is calculated on all expenditure (non-workforce and workforce).'),
                    workforce_lines_only: $t(
                        'Payroll lines include the full cost (salary + on costs). The percentage you enter here will be calculated on the total payroll cost for each period.',
                    ),
                    specific_budget_lines: $t('Applies to expenses and positions flagged as GST/VAT applicable.'),
                };
                const scopeHint = !isFixedAmount && line.application_scope ? (scopeHints[line.application_scope] ?? null) : null;
                const isPayrollScope = line.application_scope === 'workforce_lines_only';
                const rateNum = line.rate !== '' && line.rate !== null && line.rate !== undefined ? Number(line.rate) : NaN;
                const showHighRateWarning = isPayrollScope && !isNaN(rateNum) && rateNum > 100;
                return (
                    <>
                        {scopeHint && <div className="tw-bg-blue-300 tw-text-blue tw-text-base tw-px-1.5 tw-py-2 tw-rounded-lg">{scopeHint}</div>}
                        {showHighRateWarning && (
                            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-text-yellow-800 tw-text-sm tw-px-3 tw-py-2 tw-rounded-lg tw-mt-1">
                                {$t('This rate seems high. Please double-check.')}
                            </div>
                        )}
                    </>
                );
            })()}
        </div>
    );
});

export function CashflowSettingsScreen({ fetchUrl, saveUrl, taxLinesUrl, taxLinesDataUrl, onSaved }) {
    const toast = useToast();

    const [openingBalance, setOpeningBalance] = useState('');
    const [openingBalanceError, setOpeningBalanceError] = useState('');
    const [asAtDate, setAsAtDate] = useState(getFirstOfCurrentMonth());
    const [dateError, setDateError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [savedDate, setSavedDate] = useState(null);
    const [isExistingSettings, setIsExistingSettings] = useState(false);
    const [showDateWarning, setShowDateWarning] = useState(false);

    const [vatGstEnabled, setVatGstEnabled] = useState(false);
    const [vatGstRate, setVatGstRate] = useState('');
    const [vatGstFrequency, setVatGstFrequency] = useState(null);
    const [vatGstPaymentStartMonth, setVatGstPaymentStartMonth] = useState('');

    const [taxLines, setTaxLines] = useState([]);
    const [taxLineErrors, setTaxLineErrors] = useState({});
    const [vatGstErrors, setVatGstErrors] = useState({});

    const [calculationMethodOptions, setCalculationMethodOptions] = useState([]);
    const [applicationScopeOptions, setApplicationScopeOptions] = useState([]);
    const [frequencyOptions, setFrequencyOptions] = useState([]);
    const [currency, setCurrency] = useState('');

    useEffect(() => {
        const settingsRequest = axios.get(fetchUrl).then((response) => {
            if (response.data) {
                const raw = response.data.opening_balance;
                const balance = raw != null ? Number(raw) : '';
                const date = response.data.as_at_date ? response.data.as_at_date.substring(0, 10) : getFirstOfCurrentMonth();
                setOpeningBalance(balance);
                setAsAtDate(date);
                setIsExistingSettings(true);
                setSavedDate(date);

                setVatGstEnabled(response.data.vat_gst_enabled ?? false);
                setVatGstRate(response.data.vat_gst_rate ?? '');
                const savedFreq = response.data.vat_gst_frequency ?? null;
                setVatGstFrequency(savedFreq ? (VAT_GST_FREQUENCY_OPTIONS.find((o) => o.value === savedFreq) ?? null) : null);
                setVatGstPaymentStartMonth(response.data.vat_gst_payment_start_month ? response.data.vat_gst_payment_start_month.substring(0, 10) : '');
            }
        });

        const taxLinesRequest = taxLinesUrl
            ? axios.get(taxLinesUrl).then((response) => {
                  const lines = (response.data ?? []).map((l) => ({
                      ...l,
                      calculation_method: l.calculation_method ?? 'percentage',
                      rate: l.rate != null ? String(l.rate) : '',
                      application_scope: l.application_scope ?? null,
                      payment_start_month: l.payment_start_month ? l.payment_start_month.substring(0, 10) : '',
                  }));
                  setTaxLines(lines);
              })
            : Promise.resolve();

        const dataRequest = taxLinesDataUrl
            ? axios.get(taxLinesDataUrl).then((response) => {
                  setCalculationMethodOptions(response.data.calculation_methods ?? []);
                  setApplicationScopeOptions(response.data.application_scopes ?? []);
                  setFrequencyOptions(response.data.frequencies ?? []);
                  setCurrency(response.data.currency ?? '');
              })
            : Promise.resolve();

        Promise.all([settingsRequest, taxLinesRequest, dataRequest])
            .catch(() => {
                toast.error($t('Error loading cashflow settings.'));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [fetchUrl, taxLinesUrl, taxLinesDataUrl]);

    const validateDate = (value) => {
        if (!value) {
            return $t('The as at date field is required.');
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return $t('Please enter a valid date.');
        }
        const day = parseInt(value.split('-')[2], 10);
        if (day !== 1) {
            return $t('The as at date must be the first day of a month.');
        }
        return '';
    };

    /**
     * Validate tax line name and rate client-side.
     * Returns an errors object keyed by line index, or an empty object if valid.
     */
    const validateTaxLines = () => {
        const errors = {};
        taxLines.forEach((line, index) => {
            const lineErrors = {};
            if (!line.name || !line.name.trim()) {
                lineErrors.name = $t('The name field is required.');
            }
            const rate = line.rate !== '' ? Number(line.rate) : NaN;
            if (line.rate === '' || line.rate === null || line.rate === undefined || isNaN(rate)) {
                lineErrors.rate = $t('The rate field is required.');
            } else if (rate < 0) {
                lineErrors.rate = $t('The rate must be at least 0.');
            }
            if (Object.keys(lineErrors).length > 0) {
                errors[index] = lineErrors;
            }
        });
        return errors;
    };

    const doSave = () => {
        setShowDateWarning(false);
        setOpeningBalanceError('');
        setVatGstErrors({});

        const taxLineValidationErrors = validateTaxLines();
        if (Object.keys(taxLineValidationErrors).length > 0) {
            setTaxLineErrors(taxLineValidationErrors);
            return;
        }
        setTaxLineErrors({});
        setIsSaving(true);

        const payload = {
            opening_balance: openingBalance !== '' ? openingBalance : null,
            as_at_date: asAtDate,
            vat_gst_enabled: vatGstEnabled,
            vat_gst_rate: vatGstEnabled && vatGstRate !== '' ? vatGstRate : null,
            vat_gst_frequency: vatGstEnabled ? (vatGstFrequency?.value ?? null) : null,
            vat_gst_payment_start_month: vatGstEnabled && vatGstPaymentStartMonth ? vatGstPaymentStartMonth : null,
            tax_lines: taxLines.map((line) => ({
                name: line.name,
                calculation_method: line.calculation_method,
                rate: line.rate !== '' ? line.rate : null,
                application_scope: line.application_scope,
                frequency: line.frequency,
                payment_start_month: line.payment_start_month || null,
            })),
        };

        axios
            .post(saveUrl, payload)
            .then((response) => {
                toast.success($t('Cashflow settings saved successfully.'));
                setSavedDate(asAtDate);
                setIsExistingSettings(true);

                // Refresh tax lines from the response so IDs are up to date.
                if (response.data?.tax_lines) {
                    const lines = response.data.tax_lines.map((l) => ({
                        ...l,
                        calculation_method: l.calculation_method ?? 'percentage',
                        rate: l.rate != null ? String(l.rate) : '',
                        application_scope: l.application_scope ?? null,
                        payment_start_month: l.payment_start_month ? l.payment_start_month.substring(0, 10) : '',
                    }));
                    setTaxLines(lines);
                }

                if (onSaved) onSaved();
            })
            .catch((error) => {
                const generalMessage = error.response?.data?.message ?? $t('An error occurred, please try again.');
                toast.error(generalMessage);

                if (error.response?.data?.errors) {
                    const lineErrors = {};
                    const vatErrors = {};
                    const vatGstFields = ['vat_gst_rate', 'vat_gst_frequency', 'vat_gst_payment_start_month'];
                    Object.entries(error.response.data.errors).forEach(([key, messages]) => {
                        const match = key.match(/^tax_lines\.(\d+)\.(.+)$/);
                        if (match) {
                            const index = parseInt(match[1], 10);
                            const field = match[2];
                            if (!lineErrors[index]) lineErrors[index] = {};
                            lineErrors[index][field] = messages[0];
                        } else if (vatGstFields.includes(key)) {
                            vatErrors[key] = messages[0];
                        } else if (key === 'opening_balance') {
                            setOpeningBalanceError(messages[0]);
                        }
                    });
                    setTaxLineErrors(lineErrors);
                    setVatGstErrors(vatErrors);
                }
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const error = validateDate(asAtDate);
        if (error) {
            setDateError(error);
            return;
        }
        setDateError('');

        if (isExistingSettings && savedDate && asAtDate !== savedDate) {
            setShowDateWarning(true);
            return;
        }

        doSave();
    };

    const handleAddTaxLine = () => {
        setTaxLines((prev) => [...prev, newTaxLine()]);
    };

    const handleUpdateTaxLine = (index, updatedLine) => {
        setTaxLines((prev) => prev.map((l, i) => (i === index ? updatedLine : l)));
    };

    const handleDeleteTaxLine = (index) => {
        setTaxLines((prev) => prev.filter((_, i) => i !== index));
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
            <form onSubmit={handleSubmit}>
                <div className="tw-flex tw-flex-col tw-border-b-2">
                    <p className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-6">{$t('Opening Balance')}</p>
                    <div className="tw-flex tw-flex-row tw-gap-8">
                        <div className="tw-mb-4 tw-w-full">
                            <div className="tw-flex tw-items-end tw-gap-2">
                                <div className="tw-flex-1">
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <label htmlFor="cashflow-opening-balance" className="text-input__label">
                                            {$t('Amount')}
                                        </label>
                                        <NumberFormat
                                            id="cashflow-opening-balance"
                                            name="cashflow-opening-balance"
                                            thousandSeparator={true}
                                            decimalScale={2}
                                            allowNegative={true}
                                            inputMode="numeric"
                                            className={`text-input ${openingBalanceError ? 'tw-border tw-border-red-500' : 'tw-border-0'}`}
                                            placeholder="0.00"
                                            value={openingBalance}
                                            onValueChange={(values) => setOpeningBalance(values.floatValue ?? '')}
                                        />
                                        {openingBalanceError && <div className="tw-text-red-500 tw-text-sm">{openingBalanceError}</div>}
                                    </div>
                                </div>
                                {currency && <span className="tw-text-gray-500 tw-text-sm tw-pb-2">{currency}</span>}
                            </div>
                        </div>

                        <div className="tw-mb-6 tw-w-full">
                            <FormInput
                                id="cashflow-as-at-date"
                                name="cashflow-as-at-date"
                                type="date"
                                label={$t('As At Date')}
                                value={asAtDate}
                                showError={!!dateError}
                                errorText={dateError}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    if (value) {
                                        const parts = value.split('-');
                                        value = `${parts[0]}-${parts[1]}-01`;
                                    }
                                    setAsAtDate(value);
                                    setShowDateWarning(false);
                                    if (dateError) {
                                        setDateError(validateDate(value));
                                    }
                                }}
                            />
                            <p className="tw-text-gray-500 tw-text-sm tw-mt-1">{$t('Must be the first day of a month.')}</p>
                        </div>
                    </div>
                </div>

                <div className="tw-flex tw-flex-col tw-gap-6 tw-my-8">
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <h1 className="tw-text-blue tw-text-xl tw-font-semibold">{$t('Tax Lines')}</h1>
                        <h1 className="tw-font-medium tw-text-gray-600">
                            {$t('Configure tax obligation that affect your cashflow. VAT/GST uses budget lines flagged as applicable.')}
                        </h1>
                    </div>

                    {/* Built-in VAT/GST */}
                    <div className="table__card !tw-bg-grey/75">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                            <div className="tw-flex tw-flex-row tw-gap-4">
                                <span className="tw-font-bold">{$t('VAT / GST')}</span>
                                <div className="tw-bg-blue-300 tw-text-blue tw-text-xs tw-text-nowrap tw-font-medium tw-px-1.5 tw-py-1 tw-rounded-lg">
                                    {$t('Built-in')}
                                </div>
                            </div>
                            <label className="inbud-toggle">
                                <input type="checkbox" className="input tw-peer" checked={vatGstEnabled} onChange={(e) => setVatGstEnabled(e.target.checked)} />
                                <div className="switch tw-peer peer-checked:tw-bg-blue peer-checked:after:tw-left-[23px]"></div>
                            </label>
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <div className="tw-flex tw-flex-row tw-justify-between tw-gap-4 tw-mb-2">
                                <div className="tw-flex tw-flex-col tw-w-full">
                                    <div className="tw-flex tw-items-end tw-gap-2">
                                        <div className="tw-flex-1">
                                            <FormInput
                                                id="vat-gst-rate"
                                                name="vat-gst-rate"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                label={$t('VAT/GST Rate')}
                                                value={vatGstRate}
                                                disabled={!vatGstEnabled}
                                                onChange={(e) => setVatGstRate(e.target.value)}
                                                placeholder="0.00"
                                                suffix="%"
                                                labelClassName="!tw-opacity-100"
                                                inputClassName="!tw-bg-white"
                                                showError={!!vatGstErrors.vat_gst_rate}
                                                errorText={vatGstErrors.vat_gst_rate}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="tw-flex tw-flex-col tw-w-full">
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 !tw-opacity-100">
                                        {$t('Payment Frequency')}
                                    </label>
                                    <SelectField
                                        options={VAT_GST_FREQUENCY_OPTIONS}
                                        value={vatGstFrequency ? vatGstFrequency.value : null}
                                        onChange={(option) => setVatGstFrequency(option)}
                                        isDisabled={!vatGstEnabled}
                                        placeholder={$t('Select frequency')}
                                    />
                                    {vatGstErrors.vat_gst_frequency && (
                                        <div className="tw-text-red-500 tw-text-sm tw-mt-1">{vatGstErrors.vat_gst_frequency}</div>
                                    )}
                                </div>

                                <div className="tw-flex tw-flex-col tw-w-full">
                                    <FormInput
                                        id="vat-gst-payment-start-month"
                                        name="vat-gst-payment-start-month"
                                        type="date"
                                        label={$t('Payment Start Month')}
                                        value={vatGstPaymentStartMonth}
                                        disabled={!vatGstEnabled}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            if (value) {
                                                const parts = value.split('-');
                                                value = `${parts[0]}-${parts[1]}-01`;
                                            }
                                            setVatGstPaymentStartMonth(value);
                                        }}
                                        labelClassName="!tw-opacity-100"
                                        inputClassName="!tw-bg-white"
                                        showError={!!vatGstErrors.vat_gst_payment_start_month}
                                        errorText={vatGstErrors.vat_gst_payment_start_month}
                                    />
                                    <p className="tw-text-gray-500 tw-text-sm tw-mt-1">{$t('Must be the first day of a month.')}</p>
                                    <p className="tw-text-gray-500 tw-text-sm tw-mt-1">
                                        {$t(
                                            'Payments follow a cycle that starts in this month. Anything accrued in the forecast before this month is paid in this month. After that, each period is paid in the month after it ends.',
                                        )}
                                    </p>
                                </div>
                            </div>
                            <h1 className="tw-font-medium tw-text-gray-600">
                                {$t('Applies to all budget lines flagged as VAT/GST applicable. Calculated as amount x rate(VAT-exclusive)')}
                            </h1>
                        </div>
                    </div>

                    {/* User-defined tax lines */}
                    {taxLines.map((line, index) => {
                        const key = line.id ?? `new-${index}`;

                        return (
                            <TaxLineCard
                                key={key}
                                line={line}
                                onUpdate={(updated) => handleUpdateTaxLine(index, updated)}
                                onDelete={() => handleDeleteTaxLine(index)}
                                isSaving={isSaving}
                                calculationMethodOptions={calculationMethodOptions}
                                applicationScopeOptions={applicationScopeOptions}
                                frequencyOptions={frequencyOptions}
                                currency={currency}
                                errors={taxLineErrors[index] ?? {}}
                            />
                        );
                    })}

                    <button
                        type="button"
                        className="tw-flex tw-items-center tw-gap-2 tw-text-blue tw-font-medium tw-text-sm hover:tw-underline tw-self-start"
                        onClick={handleAddTaxLine}
                        disabled={isSaving}
                    >
                        <PlusIcon className="tw-w-4 tw-h-4" />
                        {$t('Add Tax Line')}
                    </button>
                </div>

                {showDateWarning && (
                    <div className="tw-mb-6">
                        <Alert type="warning">{$t('Changing the as-at date will affect all cashflow projections. Are you sure you want to continue?')}</Alert>
                        <div className="tw-flex tw-gap-3 tw-mt-3">
                            <Button variant="primary" onClick={doSave} disabled={isSaving}>
                                {isSaving ? $t('Saving...') : $t('Confirm')}
                            </Button>
                            <Button variant="secondary" onClick={() => setShowDateWarning(false)} disabled={isSaving}>
                                {$t('Cancel')}
                            </Button>
                        </div>
                    </div>
                )}

                {!showDateWarning && (
                    <button type="submit" className="button primary" disabled={isSaving}>
                        {isSaving ? $t('Saving...') : $t('Save')}
                    </button>
                )}
            </form>
        </div>
    );
}

const rootEl = document.getElementById('cashflow-settings-root');
if (rootEl) {
    const fetchUrl = rootEl.dataset.fetchUrl;
    const saveUrl = rootEl.dataset.saveUrl;
    const taxLinesUrl = rootEl.dataset.taxLinesUrl ?? '';
    const taxLinesDataUrl = rootEl.dataset.taxLinesDataUrl ?? '';
    ReactDOM.render(
        <App>
            <CashflowSettingsScreen fetchUrl={fetchUrl} saveUrl={saveUrl} taxLinesUrl={taxLinesUrl} taxLinesDataUrl={taxLinesDataUrl} />
        </App>,
        rootEl,
    );
}
